import * as React from 'react'
import * as Request from '../../utilities/request'

import { Button, Card, Col, Form, Row } from 'react-bootstrap'
import { AppContext } from '../../App'
import { DateTime } from 'luxon'
import Select from 'react-select'
import { Loading } from '../ui/Loading/Loading'
import { BillySettings, Job, XeroOrg, AccountXeroOrg } from '../../../../back-end/utilities/apiDefinitions'
import { MessageAction } from '../ui/messages/message'
import { FormText } from '../ui/Form/Text'
import { CustomOption, MenuListOverride } from '../ui/Form/Select'
import { OnboardingStep } from '../../screens/onBoarding/onBoarding'
import { useNavigate } from 'react-router-dom'

interface BillySettingsCardProps {
	updateMessages: (action: MessageAction) => void
}

const BillySettingsCard = (props: BillySettingsCardProps) => {
	const { appState } = React.useContext(AppContext)

	const [jobs, setJobs] = React.useState<Job[] | null>(null)
	const [orgs, setOrgs] = React.useState<XeroOrg[] | null>(null)

	React.useEffect(() => {
		const getData = async () => {
			const [jobRes, orgRes] = await Promise.all([
				Request.get(`job?where=JobNumber=~BA00`),
				Request.get(`organisation?where=AccountXeroOrgIsBilly==1&where=XeroOrgTenantType==ORGANISATION`),
			])
			setJobs(jobRes.data.jobs)
			setOrgs(orgRes.data.accountXeroOrgs.map((axo: AccountXeroOrg) => axo.xeroOrg))
		}

		if (appState.authState.isLoggedIn) {
			getData()
		}
	}, [appState.authState])

	return (
		<Card>
			<Card.Header>
				<h3>Billy Settings</h3>
			</Card.Header>
			<CardContent {...props} jobs={jobs} orgs={orgs} />
		</Card>
	)
}

const CardContent = (
	props: BillySettingsCardProps & {
		jobs: Job[] | null
		orgs: XeroOrg[] | null
	}
) => {
	const { appState, updateAppState } = React.useContext(AppContext)
	const navigate = useNavigate()

	const startOfCurrentMonth = DateTime.now().startOf('month').setZone('UTC', { keepLocalTime: true })
	const startOfPrevMonth = startOfCurrentMonth.minus({ months: 1 })
	const periodOptions = [
		{ value: startOfCurrentMonth.toISO(), label: startOfCurrentMonth.monthLong },
		{ value: startOfPrevMonth.toISO(), label: startOfPrevMonth.monthLong },
	]

	const currentAccountBillySettings = appState.accounts.find(
		(acc) => acc.accountID === appState.authState.attributes?.['custom:currentAccountId']
	)?.billySettings

	const [submitting, setSubmitting] = React.useState(false)
	const [agedWipDate, setAgedWipDate] = React.useState(
		currentAccountBillySettings && periodOptions.some((option) => option.value === currentAccountBillySettings.agedWipDate)
			? currentAccountBillySettings.agedWipDate
			: ''
	)
	const [serviceFee, setServiceFee] = React.useState<number>(currentAccountBillySettings?.serviceFee ?? 0)
	const [costRateIsRequired, setCostRateIsRequired] = React.useState<boolean>(currentAccountBillySettings?.costRateIsRequired ?? false)
	const [financialYearEndDay, setFinancialYearEndDay] = React.useState<number>(currentAccountBillySettings?.financialYearEndDay ?? 1)
	const [financialYearEndMonth, setFinancialYearEndMonth] = React.useState<number>(currentAccountBillySettings?.financialYearEndMonth ?? 1)
	const [invoiceMaxDaysOutstanding, setInvoiceMaxDaysOutstanding] = React.useState<number>(currentAccountBillySettings?.invoiceMaxDaysOutstanding ?? 90)
	const [reportJobXpmIDsNonChargeable, setReportJobXpmIDsNonChargeable] = React.useState(currentAccountBillySettings?.reportJobXpmIDsNonChargeable ?? { jobXpmIDs: [] })
	const [reportJobXpmIDsExcepted, setReportJobXpmIDsExcepted] = React.useState(currentAccountBillySettings?.reportJobXpmIDsExcepted ?? { jobXpmIDs: [] })
	const [defaultInvoiceXeroOrgID, setDefaultInvoiceXeroOrgID] = React.useState(currentAccountBillySettings?.defaultInvoiceXeroOrgID ?? '')
	const [syncErrorEmails, setSyncErrorEmails] = React.useState<string>((currentAccountBillySettings?.syncErrorEmails ?? []).join(','))

	React.useEffect(() => {
		if (appState.onBoardingStep === OnboardingStep.GeneralSettings) {
			props.updateMessages({
				type: 'add',
				data: {
					severity: 'info',
					message:
						'These settings will impact how invoices are raised, how your reporting looks.\nNote: you wont be able to update the report jobs until your XPM sync is complete.',
					timeout: 0,
					dismissible: true,
				},
			})
		}
	}, [appState.onBoardingStep])

	if (appState.accounts.length === 0 || !props.jobs || !props.orgs) {
		return (
			<div className="center-flex p-5">
				<Loading show />
			</div>
		)
	}

	if (!currentAccountBillySettings) {
		return (
			<div className="center-flex p-5">
				<span>No Billy settings found for this account.</span>
			</div>
		)
	}

	const monthOptions = Array.from(Array(12)).map((_, index) => ({
		value: String(index + 1),
		label: DateTime.fromObject({ month: index + 1 }).toFormat('LLLL'),
	}))

	const dayOptions = Array.from(Array(DateTime.fromObject({ month: financialYearEndMonth }).daysInMonth)).map((_, index) => ({
		value: String(index + 1),
		label: String(index + 1),
	}))

	const maxDaysOptions = ['30', '45', '90', '150', '180'].map((val) => ({
		value: val,
		label: val,
	}))

	const jobOptions = props.jobs.sort((a, b) => (a.number < b.number ? -1 : 1)).map((j) => ({ value: j.xpmID, label: `${j.number} - ${j.name}` }))
	const orgOptions = props.orgs.sort((a, b) => ((a.name || '') < (b.name || '') ? -1 : 1)).map((xo) => ({ value: xo.xeroOrgID, label: xo.name }))

	const handleBillySettingsSave = async () => {
		setSubmitting(true)

		try {
			await Request.put('billySettings', {
				agedWipDate,
				serviceFee,
				costRateIsRequired,
				financialYearEndDay,
				financialYearEndMonth,
				invoiceMaxDaysOutstanding,
				reportJobXpmIDsNonChargeable,
				reportJobXpmIDsExcepted,
				syncErrorEmails: syncErrorEmails.split(',').map((x) => x.trim()),
				defaultInvoiceXeroOrgID,
			} as Partial<BillySettings>)
			props.updateMessages({
				type: 'add',
				data: {
					severity: 'success',
					message: 'Successfully updated Billy Settings',
					timeout: 3000,
					dismissible: true,
				},
			})

			// check if we are in onBoarding
			if (appState.onBoardingStep && appState.onBoardingStep === OnboardingStep.GeneralSettings) {
				updateAppState({ state: 'onboarding', data: { ...appState, onBoardingStep: OnboardingStep.GeneralSettings + 1 } })
				navigate('/onboarding')
			}
		} catch (err) {
			props.updateMessages({
				type: 'add',
				data: {
					severity: 'danger',
					message: 'Unable to update Billy Settings',
					timeout: 3000,
					dismissible: true,
				},
			})
		}

		setSubmitting(false)
	}

	return (
		<>
			<Card.Body>
				<Row>
					<Col>
						<Form.Label>Current Period</Form.Label>
						<Select
							placeholder="Select Period..."
							options={periodOptions}
							value={periodOptions.find((option) => option.value === agedWipDate)}
							onChange={(option) => setAgedWipDate(option?.value || '')}
						/>
					</Col>
				</Row>
				<Row className="pt-2">
					<Col>
						<Form.Label>Service Fee (%)</Form.Label>
						<FormText name="serviceFee" value={serviceFee.toFixed(2)} onChange={({ target }) => setServiceFee(Number(target.value))} />
					</Col>
					<Col>
						<Form.Label>Cost Rate Required</Form.Label>
						<Form.Check
							type="checkbox"
							name="costRateIsRequired"
							checked={costRateIsRequired}
							onChange={() => {
								setCostRateIsRequired(!costRateIsRequired)
							}}
							className="custom-checkbox"
							label=""
						/>
					</Col>
				</Row>
				<Row className="pt-2">
					<Col>
						<Form.Label>Financial Year End Day/Month</Form.Label>
						<Row>
							<Col>
								<Select
									options={dayOptions}
									value={dayOptions.find((option) => option.value === financialYearEndDay.toString())}
									onChange={(option) => setFinancialYearEndDay(Number(option?.value || 1))}
								/>
							</Col>
							<Col>
								<Select
									options={monthOptions}
									value={monthOptions.find((option) => option.value === financialYearEndMonth.toString())}
									onChange={(option): void => {
										setFinancialYearEndDay(option?.value ? DateTime.fromObject({ month: Number(option.value) }).endOf('month').day : 1)
										setFinancialYearEndMonth(Number(option?.value || 1))
									}}
								/>
							</Col>
						</Row>
					</Col>
				</Row>
				<Row className="pt-2">
					<Col>
						<Form.Label>Invoice Maximum Outstanding Days</Form.Label>
						<Select
							options={maxDaysOptions}
							value={maxDaysOptions.find((option) => option.value === invoiceMaxDaysOutstanding.toString())}
							onChange={(option) => setInvoiceMaxDaysOutstanding(Number(option?.value) || 90)}
						/>
					</Col>
				</Row>
				<Row className="pt-2">
					<Col>
						<Form.Label>Report Jobs - Hours Non-Chargeable</Form.Label>
						<Select
							options={jobOptions.filter((option) => !reportJobXpmIDsExcepted.jobXpmIDs.some((jobXpmID) => jobXpmID === option.value))}
							value={jobOptions.filter((option) => reportJobXpmIDsNonChargeable.jobXpmIDs.some((jobXpmID) => jobXpmID === option.value))}
							onChange={(options) => setReportJobXpmIDsNonChargeable({ jobXpmIDs: options.map((opt) => opt.value) })}
							isMulti
							// Hide items if more than 200 would be displayed
							components={{ Option: CustomOption, MenuList: MenuListOverride }}
							styles={{
								singleValue: (base, state) => ({
									// TS def for the base CSS properties is too broad, any cast to stop singleValue complaining
									// eslint-disable-next-line @typescript-eslint/no-explicit-any
									...(base as any),
									display: state.selectProps.menuIsOpen ? 'none' : 'block',
								}),
							}}
						/>
					</Col>
					<Col>
						<Form.Label>Report Jobs - Hours Excepted</Form.Label>
						<Select
							options={jobOptions.filter((option) => !reportJobXpmIDsNonChargeable.jobXpmIDs.some((jobXpmID) => jobXpmID === option.value))}
							value={jobOptions.filter((option) => reportJobXpmIDsExcepted.jobXpmIDs.some((jobXpmID) => jobXpmID === option.value))}
							onChange={(options) => setReportJobXpmIDsExcepted({ jobXpmIDs: options.map((opt) => opt.value) })}
							isMulti
							// Hide items if more than 200 would be displayed
							components={{ Option: CustomOption, MenuList: MenuListOverride }}
							styles={{
								singleValue: (base, state) => ({
									// TS def for the base CSS properties is too broad, any cast to stop singleValue complaining
									// eslint-disable-next-line @typescript-eslint/no-explicit-any
									...(base as any),
									display: state.selectProps.menuIsOpen ? 'none' : 'block',
								}),
							}}
						/>
					</Col>
				</Row>
				<Row>
					<Col>
						<Form.Label>Default Invoice Xero Org</Form.Label>
						<Select
							options={orgOptions}
							value={orgOptions.filter((option) => option.value === defaultInvoiceXeroOrgID)}
							onChange={(option) => setDefaultInvoiceXeroOrgID(option!.value!)}
							components={{ Option: CustomOption, MenuList: MenuListOverride }}
							styles={{
								singleValue: (base, state) => ({
									// TS def for the base CSS properties is too broad, any cast to stop singleValue complaining
									// eslint-disable-next-line @typescript-eslint/no-explicit-any
									...(base as any),
									display: state.selectProps.menuIsOpen ? 'none' : 'block',
								}),
							}}
						/>
					</Col>
				</Row>
				<Row>
					<Col>
						<Form.Label>Sync Error Email Recipients</Form.Label>
						<FormText name="syncErrorEmails" type="text" onChange={(e) => setSyncErrorEmails(e.target.value)} value={syncErrorEmails} />
					</Col>
				</Row>
			</Card.Body>
			<Card.Footer>
				<Row>
					<Col />
					<Col sm="auto">
						<Button variant="primary" onClick={handleBillySettingsSave} disabled={submitting || agedWipDate === '' || isNaN(serviceFee)}>
							{submitting ? <Loading show size="sm" /> : 'Save'}
						</Button>
					</Col>
				</Row>
			</Card.Footer>
		</>
	)
}

export default BillySettingsCard
